import type { GetConsultationTypeReturn } from '@/components/PatientPageV2/utils/getConsultationType';
import { ConsultType, getConsultationType } from '@/components/PatientPageV2/utils/getConsultationType';
import { FF_ENABLE_INITIAL_DR_PAYMENT } from '@/constants/featureFlags';
import { isAdmin, isDoctor, isManager, isNurse, isSuperAdmin } from '@/data/service/authService';
import { useFeatureFlags } from '@/hooks/flags';
import { useMemo } from 'react';

type UserRoles = {
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isNurse: boolean;
  isDoctor: boolean;
  isManager: boolean;
};

const userIsAdminOrClinician = (userRoles: UserRoles) => {
  return (
    userRoles.isAdmin ||
    userRoles.isSuperAdmin ||
    userRoles.isDoctor ||
    userRoles.isNurse ||
    userRoles.isManager ||
    false
  );
};

const checkCanUserChargeConsult = (
  userRoles: UserRoles,
  consultAndClinicianType: GetConsultationTypeReturn,
  ffEnableInitialDrPayment: boolean,
) => {
  if (ffEnableInitialDrPayment) {
    return userRoles.isAdmin || userRoles.isSuperAdmin || userRoles.isDoctor || userRoles.isManager || false;
  }
  if (consultAndClinicianType === ConsultType.InitialNurse) {
    return userRoles.isAdmin || userRoles.isSuperAdmin || userRoles.isNurse || userRoles.isManager || false;
  }
  if (consultAndClinicianType === ConsultType.FollowUpDoctor) {
    return userIsAdminOrClinician(userRoles);
  }
  return false;
};

export const useCanUserChargeConsult = ({
  consultationType,
  doctorId,
}: {
  consultationType?: string;
  doctorId?: string | number;
}) => {
  const { flags } = useFeatureFlags();
  const ffEnableInitialDrPayment = flags[FF_ENABLE_INITIAL_DR_PAYMENT];

  const userRoles = useMemo<UserRoles>(() => {
    return {
      isAdmin: isAdmin(),
      isSuperAdmin: isSuperAdmin(),
      isNurse: isNurse(),
      isDoctor: isDoctor(),
      isManager: isManager(),
    };
  }, []);

  const consultAndClinicianType: GetConsultationTypeReturn = getConsultationType({
    consult_type: consultationType,
    doctor_id: doctorId,
  });

  const canUserChargeConsult = checkCanUserChargeConsult(userRoles, consultAndClinicianType, ffEnableInitialDrPayment);

  return {
    canUserChargeConsult,
    consultAndClinicianType,
  };
};
